// The emoji picker of the Conversations panel (owner, 2026-10-18: « Panel
// composer: emoji picker. React to a client message (❤️ 👍 🙏 😂 😮 + any
// emoji) »).
//
// ⚠️ NO NPM DEPENDENCY, ON PURPOSE. Full pickers weigh hundreds of KB of data
// and fonts for a box the team opens a few times a day. This is a static list
// of the emojis a photo-album shop actually writes — faces, hands, hearts,
// celebrations, travel, the things in an album — drawn by the system font.
// Any other emoji still pastes into the box as text.
//
// Two uses, one component: the composer inserts at the cursor
// (`insertAtCursor`, below), the bubble's « + » sends a reaction.
//
// Guard: EmojiPicker.test.tsx
import { useEffect, useRef, useState } from 'react';
import { useT } from '@/lib/i18n';

/** WhatsApp's own quick row — the reaction palette's first five. */
export const QUICK_REACTIONS = ['❤️', '👍', '🙏', '😂', '😮'] as const;

export const EMOJI_GROUPS: ReadonlyArray<{ id: string; label: string; icon: string; emojis: readonly string[] }> = [
  { id: 'faces', label: 'Visages', icon: '😊', emojis: [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋',
    '😛', '😜', '🤪', '😝', '🤗', '🤭', '🤫', '🤔', '🤐', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮', '😯', '😲', '😳',
    '🥺', '😢', '😭', '😤', '😠', '😡', '😱', '😨', '😰', '😥', '😓', '🤯', '😴', '🤤', '😷', '🤒', '🥳', '😎', '🤩', '🥹',
  ] },
  { id: 'hands', label: 'Mains', icon: '👍', emojis: [
    '👍', '👎', '👌', '🤌', '✌️', '🤞', '🤟', '🤙', '👋', '🤚', '✋', '🖐️', '👏', '🙌', '👐', '🤲', '🙏', '🤝', '💪', '☝️',
    '👆', '👇', '👈', '👉', '✍️', '🫶', '🫡', '🤷', '🙋', '💁',
  ] },
  { id: 'hearts', label: 'Cœurs', icon: '❤️', emojis: [
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '❣️', '💔', '❤️‍🔥', '💯',
  ] },
  { id: 'party', label: 'Fêtes', icon: '🎉', emojis: [
    '🎉', '🎊', '🥳', '🎂', '🎁', '🎈', '🎀', '💐', '🌹', '🌸', '🌷', '🌺', '💍', '👰', '🤵', '💒', '🕋', '🕌', '🌙', '⭐',
    '✨', '🌟', '🔥', '🎓', '👶', '🍼', '🏆', '🥂',
  ] },
  { id: 'things', label: 'Objets', icon: '📸', emojis: [
    '📸', '📷', '🖼️', '📖', '📚', '📔', '📦', '🚚', '✈️', '🧳', '🏖️', '🏝️', '🗺️', '🏔️', '📍', '📅', '⏰', '⏳', '💰', '💳',
    '📱', '💬', '📩', '✅', '☑️', '❌', '⚠️', '❓', '❗', '👀',
  ] },
];

/**
 * Put `insert` where the cursor is (or over the selection), and say where the
 * cursor goes next. Pure — the composer's textarea is controlled by React, so
 * the new value and caret are applied by the caller.
 */
export function insertAtCursor(value: string, start: number | null | undefined, end: number | null | undefined, insert: string): { value: string; caret: number } {
  const len = value.length;
  const a = Number.isFinite(start as number) ? Math.max(0, Math.min(len, start as number)) : len;
  const b = Number.isFinite(end as number) ? Math.max(a, Math.min(len, end as number)) : a;
  return { value: value.slice(0, a) + insert + value.slice(b), caret: a + insert.length };
}

/**
 * The picker itself: category tabs, a grid. `onPick` gets one emoji; `onClose`
 * on Escape or a click outside. Positioned by the caller (absolute).
 */
export function EmojiPicker({ onPick, onClose, label, className = '' }: {
  onPick: (emoji: string) => void;
  onClose: () => void;
  label?: string;
  className?: string;
}) {
  const { t } = useT();
  const [group, setGroup] = useState(EMOJI_GROUPS[0].id);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [onClose]);
  const shown = EMOJI_GROUPS.find((g) => g.id === group) ?? EMOJI_GROUPS[0];
  return (
    <div ref={ref} role="dialog" aria-label={label ?? t('Emojis')} data-emoji-picker
      className={`z-30 w-[300px] max-w-[calc(100vw-2rem)] rounded-xl border border-black/10 dark:border-[var(--wa-frame)] bg-white dark:bg-[var(--wa-bar)] shadow-lg p-2 ${className}`}>
      <div role="tablist" className="flex gap-1 mb-1.5">
        {EMOJI_GROUPS.map((g) => (
          <button key={g.id} type="button" role="tab" aria-selected={g.id === group} title={t(g.label)} aria-label={t(g.label)}
            onClick={() => setGroup(g.id)}
            className={`h-8 w-8 rounded-lg text-[18px] leading-none cursor-pointer ${g.id === group ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}>
            {g.icon}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-8 gap-0.5 max-h-48 overflow-y-auto overscroll-contain" data-no-ptr>
        {shown.emojis.map((e) => (
          <button key={e} type="button" data-emoji={e} onClick={() => onPick(e)} aria-label={e}
            className="h-8 w-8 rounded-md text-[20px] leading-none hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00a884]">
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}
